import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import SEO_META from "./seoMeta.json";

const SITE = window.location.origin;
const OG_IMAGE = `${SITE}${SEO_META.ogImagePath}`;
const DEFAULT_TITLE = SEO_META.defaultTitle;
const DEFAULT_DESC = SEO_META.defaultDesc;

const IS_PROD_HOST = /^(www\.)?thebiologytrader\.com$/i.test(window.location.hostname);

// Titles, descriptions and canonicals live in seoMeta.json so the build-time
// prerender emits the identical head that PageMeta sets at runtime.
const META = SEO_META.pages;

const EBOOK_JSONLD = SEO_META.ebookJsonLd;

const EBOOK_JSONLD_ID = "ebook-jsonld";

const upsertMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const removeMeta = (attr, key) => {
  document.head.querySelectorAll(`meta[${attr}="${key}"]`).forEach((el) => el.remove());
};

const upsertCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!href) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }
  el.href = href;
};

const upsertEbookJsonLd = (enabled) => {
  const existing = document.getElementById(EBOOK_JSONLD_ID);
  if (!enabled) {
    if (existing) existing.remove();
    return;
  }
  if (existing) return;
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.id = EBOOK_JSONLD_ID;
  s.textContent = JSON.stringify(EBOOK_JSONLD);
  document.head.appendChild(s);
};

export function PageMeta() {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname.replace(/\/+$/, "") || "/";
    const isAdmin = path === "/admin" || path.startsWith("/admin/");
    const meta = META[path];
    const title = meta ? meta.title : DEFAULT_TITLE;
    const description = meta ? meta.description : DEFAULT_DESC;
    const ogTitle = meta && meta.ogTitle ? meta.ogTitle : title;
    const ogDescription = meta && meta.ogDescription ? meta.ogDescription : description;
    const url = `${SITE}${path === "/" ? "/" : path}`;
    document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", ogTitle);
    upsertMeta("property", "og:description", ogDescription);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", OG_IMAGE);
    upsertMeta("property", "og:image:width", "1731");
    upsertMeta("property", "og:image:height", "909");
    upsertMeta("property", "og:image:alt", "The Biology Trader — Trade within the limits of your body.");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:image", OG_IMAGE);

    // Canonicals: Change 4. Absolute production URLs on the routes that opt in
    // via META[path].canonical. Skipped for /thank-you, /leave, /admin/*,
    // /track-record* and unknown routes.
    upsertCanonical(meta && meta.canonical ? meta.canonical : null);

    // Robots: Change 3 (per-route) merged with Change 5 (preview-domain
    // blanket noindex). Only one <meta name="robots"> is ever managed, so
    // Changes 3 and 5 cannot produce duplicates.
    let robotsContent = null;
    if (isAdmin) {
      robotsContent = "noindex, nofollow";
    } else if (path === "/thank-you" || path === "/leave") {
      robotsContent = "noindex, follow";
    } else if (!IS_PROD_HOST) {
      robotsContent = "noindex";
    }
    if (robotsContent) upsertMeta("name", "robots", robotsContent);
    else removeMeta("name", "robots");

    // Change 8: Book JSON-LD only on /ebook.
    upsertEbookJsonLd(path === "/ebook");
  }, [location.pathname]);
  return null;
}
